import React from 'react';
import { Grid, TextField, MenuItem, InputAdornment, Select, FormControl, Box, Button } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import MapIcon from '@mui/icons-material/Map';

// Utils
// Parses a lat/long pair out of a pasted Google Maps URL, e.g.
//   https://www.google.com/maps/place/.../@53.3498,-6.2603,15z
//   https://www.google.com/maps?q=53.3498,-6.2603
// NOTE: shortened links (maps.app.goo.gl/...) don't contain coordinates in
// the URL itself, so this won't work for short links.
export const parseLatLngFromGoogleMapsUrl = (url) => {
  if (!url) return null;
  const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (atMatch) return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };

  const qMatch = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (qMatch) return { lat: parseFloat(qMatch[1]), lng: parseFloat(qMatch[2]) };

  const llMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (llMatch) return { lat: parseFloat(llMatch[1]), lng: parseFloat(llMatch[2]) };

  return null;
};

// Builds a Google Maps link back out of a stored lat/lng pair — used to
// prefill the input when a record already has coordinates but no link on file.
export const buildGoogleMapsLink = (lat, lng) => {
  if (lat === undefined || lat === null || lng === undefined || lng === null || lat === '' || lng === '') return '';
  return `https://www.google.com/maps?q=${lat},${lng}`;
};


const CONTACT_TYPE_OPTIONS = ['Supplier', 'Grower', 'Sawmills'];

const useStyles = makeStyles(() => ({
  phonePrefixSelect: {
    marginRight: 8,
    '& .MuiSelect-select': {
      padding: '8.5px 12px',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
  },
}));

/**
 * Contact + property identity. One record here maps 1:1 to one
 * property/license - if the same person owns a second forest, that's a
 * second Contact row, not a second Enquiry on this one. That's why
 * License/Address/Coordinates all live here rather than on the Enquiry tab.
 */
export default function GeneralDetailsForm({
  formData,
  errors,
  onChange,
  onStageChange,
  onDropPinChange,
  teams = [],
  stages = [],
  teamMembers = [],
  isEdit = false,
  readOnly = false,
}) {
  const classes = useStyles();

  return (
    <Grid container spacing={2}>
      {/* Pipeline meta */}
      <Grid item xs={12} sm={4}>
        <TextField
          select
          required
          fullWidth
          size="small"
          label="Team Name"
          value={formData.crmTeamId}
          onChange={onChange('crmTeamId')}
          disabled={isEdit || readOnly}>
          {teams.map(t => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          select
          required
          fullWidth
          size="small"
          disabled={readOnly}
          label="Stage Name"
          value={formData.crmStageName}
          onChange={onStageChange}>
          {stages.map(s => (
            <MenuItem key={s.value || s.StageId || s.label} value={s.label}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          select
          required
          fullWidth
          size="small"
          disabled={readOnly}
          label="Sales Rep"
          value={formData.crmSalesRep}
          onChange={onChange('crmSalesRep')}>
          {teamMembers.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          select
          required
          fullWidth
          size="small"
          label="Contact Type"
          disabled={readOnly}
          value={formData.crmProjectType || ''}
          onChange={onChange('crmProjectType')}>
          {CONTACT_TYPE_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Names */}
      <Grid item xs={12} sm={4}>
        <TextField
          required
          fullWidth
          size="small"
          disabled={readOnly}
          label="First Name"
          value={formData.crmContactFirst}
          onChange={onChange('crmContactFirst')}
          error={Boolean(errors.crmContactFirst)}
          helperText={errors.crmContactFirst}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Middle Name"
          value={formData.crmContactMiddle}
          onChange={onChange('crmContactMiddle')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Last Name"
          value={formData.crmContactLast}
          onChange={onChange('crmContactLast')}
          error={Boolean(errors.crmContactLast)}
          helperText={errors.crmContactLast}
        />
      </Grid>

      {/* Title / Company / Industry */}
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Title"
          value={formData.crmTitle}
          onChange={onChange('crmTitle')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Company"
          value={formData.crmCompany}
          onChange={onChange('crmCompany')}
          error={Boolean(errors.crmCompany)}
          helperText={errors.crmCompany}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Industry"
          value={formData.crmIndustry}
          onChange={onChange('crmIndustry')}
        />
      </Grid>

      {/* Phone / Email / LinkedIn */}
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth size="small" disabled={readOnly} error={Boolean(errors.crmContactPhone)}>
          <TextField
            size="small"
            disabled={readOnly}
            label="Phone"
            required
            value={formData.crmContactPhone}
            onChange={onChange('crmContactPhone')}
            error={Boolean(errors.crmContactPhone)}
            helperText={errors.crmContactPhone}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Select
                    variant="standard" disableUnderline disabled={readOnly}
                    value={formData.crmPhoneCountryCode}
                    onChange={onChange('crmPhoneCountryCode')}
                    className={classes.phonePrefixSelect}>
                    <MenuItem value="+353">🇮🇪 +353</MenuItem>
                    <MenuItem value="+44">🇬🇧 +44</MenuItem>
                  </Select>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Email"
          value={formData.crmContactEmail}
          onChange={onChange('crmContactEmail')}
          error={Boolean(errors.crmContactEmail)}
          helperText={errors.crmContactEmail}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" disabled={readOnly} label="Linkedin"
          value={formData.crmLinkedInProfile} onChange={onChange('crmLinkedInProfile')} />
      </Grid>

      {/* Address */}
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Address Line 1"
          value={formData.crmAddressLine1}
          onChange={onChange('crmAddressLine1')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Address Line 2"
          value={formData.crmAddressLine2}
          onChange={onChange('crmAddressLine2')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Town"
          value={formData.crmAddressTown}
          onChange={onChange('crmAddressTown')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="City"
          value={formData.crmAddressCity}
          onChange={onChange('crmAddressCity')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField select fullWidth size="small" disabled={readOnly} label="Country"
          value={formData.crmAddressCountry} onChange={onChange('crmAddressCountry')}
          error={Boolean(errors.crmAddressCountry)} helperText={errors.crmAddressCountry}>
          <MenuItem value="Ireland">Ireland</MenuItem>
          <MenuItem value="United Kingdom">United Kingdom</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Zipcode"
          value={formData.crmAddressZip}
          onChange={onChange('crmAddressZip')}
        />
      </Grid>

      {/* License - consolidated here since it's 1:1 with the record */}
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" disabled={readOnly} label="License No"
          value={formData.fellingLicenseNo} onChange={onChange('fellingLicenseNo')} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField select fullWidth size="small" disabled={readOnly} label="Felling License Status"
          value={formData.fellingLicenseStatus} onChange={onChange('fellingLicenseStatus')}>
          <MenuItem value="">—</MenuItem>
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      </Grid>


      {/* Coordinates */}
      <Grid item xs={12} sm={4}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<MapIcon />}
          href="https://www.google.com/maps"
          target="_blank"
          rel="noopener"
          disabled={readOnly}
          sx={{ whiteSpace: 'nowrap', mt: 0.25 }}>
          Open Google Maps
        </Button>
      </Grid>

      <Grid item xs={12} sm={8}>
        <TextField
          fullWidth
          size="small"
          disabled={readOnly}
          label="Forest Location (paste Google Maps link)"
          placeholder="Paste the full Google Maps link here"
          helperText={
            formData.dropPinLatitude && formData.dropPinLongitude
              ? `Detected: ${formData.dropPinLatitude}, ${formData.dropPinLongitude}`
              : 'Use the full link (not a shortened maps.app.goo.gl link) so we can read the coordinates'
          }
          value={formData.dropPin || ''}
          onChange={e => onDropPinChange(e.target.value)}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={2}
          size="small"
          disabled={readOnly}
          label="Background Info"
          value={formData.crmBackgroundInfo}
          onChange={onChange('crmBackgroundInfo')}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" disabled={readOnly} label="Website"
          value={formData.crmContactWebsite} onChange={onChange('crmContactWebsite')} />
      </Grid>
    </Grid>
  );
}